/**
 * PreviewSVG Component
 *
 * Modern replacement for the deprecated PreviewSVG from @alchemiesolns/svg-container.
 * Provides static, read-only SVG rendering of molecular structures using the new
 * svg-interactive architecture.
 *
 * Based on ReadOnlyExample pattern: MolecularSVGProvider + SVGCanvas
 */

import { forwardRef, useMemo } from 'react';
import { MolecularSVGProvider } from '../contexts/MolecularSVGContext';
import { SVGCanvas } from './SVGCanvas';
import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';

export interface PreviewSVGProps {
  /** Opaque configuration string for the molecular structure */
  config: string;
  /** SVG width in pixels (default: 120) */
  svgWidth?: number;
  /** SVG height in pixels (default: 90) */
  svgHeight?: number;
  /** Padding around content in SVG units (default: 3) */
  padding?: number;
}

/**
 * Static SVG preview component for molecular structures.
 *
 * Drop-in replacement for the old PreviewSVG from svg-container.
 * Uses modern svg-interactive architecture with MolecularSVGProvider + SVGCanvas.
 *
 * Features:
 * - Read-only rendering (no zoom/pan/selection/motion)
 * - Supports forwardRef for SVG element access (e.g., for serialization)
 * - Error handling with graceful fallback
 * - Treats config strings as opaque (uses MolecularWorkspace.fromConfigString)
 *
 * @example
 * ```tsx
 * <PreviewSVG
 *   config="a*C,H,H,H,H|b*0:1:1:l,0:2:1:l,0:3:1:l,0:4:1:l|p*0:0,1:1,1:-1,-1:1,-1:-1"
 *   svgWidth={120}
 *   svgHeight={90}
 * />
 * ```
 */
export const PreviewSVG = forwardRef<SVGSVGElement, PreviewSVGProps>(
  ({ config, svgWidth = 120, svgHeight = 90, padding = 3 }, ref) => {
    // Parse config string to workspace (treats config as opaque)
    const workspace = useMemo(() => {
      if (!config) {
        // Return empty workspace for empty config
        return new MolecularWorkspace();
      }

      try {
        return fromConfigString(config);
      } catch (error) {
        console.error('[PreviewSVG] Failed to parse config string:', error);
        // Return empty workspace as fallback
        return new MolecularWorkspace();
      }
    }, [config]);

    return (
      <MolecularSVGProvider
        workspace={workspace}
        svgWidth={svgWidth}
        svgHeight={svgHeight}
        padding={padding}
      >
        <SVGCanvas ref={ref} />
      </MolecularSVGProvider>
    );
  }
);

PreviewSVG.displayName = 'PreviewSVG';
